import { useState, useRef, useEffect, useCallback } from "react";
import LessonSummaryModal from "../components/LessonSummaryModal";

// ─── Styles ───────────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Mono:wght@400;500&family=DM+Sans:wght@300;400;500&display=swap');

  .ls-root {
    --bg: #0a0c10;
    --surface: #12151c;
    --border: #1e2330;
    --accent: #4fc3f7;
    --gold: #C8973A;
    --text: #e8eaf0;
    --muted: #5a6070;
    --live: #4caf50;
    --live-glow: rgba(76,175,80,0.3);
    --error: #ef5350;
    font-family: 'DM Sans', sans-serif;
    background: var(--bg);
    color: var(--text);
    height: 100vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
  }

  .ls-root::before {
    content: '';
    position: fixed;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 0;
    opacity: 0.4;
  }

  .ls-topbar {
    position: relative;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 24px;
    border-bottom: 1px solid var(--border);
    background: rgba(18,21,28,0.9);
    backdrop-filter: blur(10px);
    gap: 16px;
    flex-wrap: wrap;
  }

  .ls-brand { display: flex; align-items: center; gap: 10px; }
  .ls-brand-icon {
    width: 36px; height: 36px;
    background: linear-gradient(135deg, var(--accent), #0288d1);
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px;
    box-shadow: 0 0 16px rgba(79,195,247,0.3);
  }
  .ls-brand-name { font-family: 'DM Serif Display', serif; font-size: 18px; letter-spacing: -0.3px; color: var(--text); }
  .ls-brand-name span { color: var(--accent); }

  .ls-api-row { display: flex; align-items: center; gap: 8px; flex: 1; max-width: 380px; }
  .ls-api-label { font-size: 11px; font-weight: 500; color: var(--muted); text-transform: uppercase; letter-spacing: 0.8px; white-space: nowrap; }
  .ls-api-input {
    flex: 1; background: var(--bg); border: 1px solid var(--border);
    border-radius: 8px; padding: 7px 12px; color: var(--text);
    font-family: 'DM Mono', monospace; font-size: 12px; outline: none; transition: border-color 0.2s;
  }
  .ls-api-input:focus { border-color: var(--accent); }

  .ls-font-control { display: flex; align-items: center; gap: 6px; }
  .ls-font-label { font-size: 11px; color: var(--muted); text-transform: uppercase; letter-spacing: 0.8px; }
  .ls-slider { -webkit-appearance: none; width: 80px; height: 4px; background: var(--border); border-radius: 2px; outline: none; cursor: pointer; }
  .ls-slider::-webkit-slider-thumb { -webkit-appearance: none; width: 14px; height: 14px; border-radius: 50%; background: var(--accent); cursor: pointer; }

  .ls-controls { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

  .ls-status {
    display: flex; align-items: center; gap: 6px;
    padding: 5px 12px; border-radius: 999px; font-size: 12px; font-weight: 500;
    border: 1px solid var(--border); background: var(--surface); color: var(--muted);
    transition: all 0.3s; white-space: nowrap;
  }
  .ls-status.live  { border-color: var(--live);  color: var(--live);  background: var(--live-glow); box-shadow: 0 0 12px var(--live-glow); }
  .ls-status.error { border-color: var(--error); color: var(--error); }

  .ls-dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; flex-shrink: 0; }
  .ls-dot.pulse { animation: ls-pulse 1.5s ease-in-out infinite; }
  @keyframes ls-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.4;transform:scale(.7)} }

  .ls-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 8px 18px; border-radius: 8px; border: none;
    font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 500;
    cursor: pointer; transition: all 0.2s; white-space: nowrap;
  }
  .ls-btn-primary { background: linear-gradient(135deg, var(--accent), #0288d1); color: #000; box-shadow: 0 0 20px rgba(79,195,247,0.25); }
  .ls-btn-primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 0 28px rgba(79,195,247,0.4); }
  .ls-btn-stop { background: rgba(239,83,80,0.12); color: var(--error); border: 1px solid rgba(239,83,80,0.3); }
  .ls-btn-stop:hover:not(:disabled) { background: rgba(239,83,80,0.2); }
  .ls-btn-clear { background: rgba(255,255,255,0.04); color: var(--muted); border: 1px solid var(--border); }
  .ls-btn-clear:hover:not(:disabled) { background: rgba(255,255,255,0.08); color: var(--text); }
  .ls-btn-gold { background: rgba(200,151,58,0.12); color: var(--gold); border: 1px solid rgba(200,151,58,0.35); }
  .ls-btn-gold:hover:not(:disabled) { background: rgba(200,151,58,0.22); }
  .ls-btn-dl { background: rgba(255,255,255,0.04); color: var(--muted); border: 1px solid var(--border); font-size: 12px; padding: 6px 14px; }
  .ls-btn-dl:hover:not(:disabled) { background: rgba(255,255,255,0.08); color: var(--text); }
  .ls-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  /* DISPLAY */
  .ls-display { position: relative; z-index: 5; flex: 1; display: flex; flex-direction: column; padding: 40px 60px 20px; overflow: hidden; }

  .ls-transcript-wrapper { flex: 1; display: flex; flex-direction: column; justify-content: flex-end; overflow: hidden; position: relative; }
  .ls-transcript-wrapper::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 80px;
    background: linear-gradient(to bottom, var(--bg), transparent); z-index: 2; pointer-events: none;
  }

  .ls-scroll { overflow-y: auto; max-height: 100%; padding-top: 80px; scrollbar-width: none; }
  .ls-scroll::-webkit-scrollbar { display: none; }
  .ls-lines { display: flex; flex-direction: column; gap: 4px; }

  .ls-line-final { font-family: 'DM Serif Display', serif; color: var(--text); line-height: 1.35; animation: ls-fadein 0.3s ease; }
  .ls-line-interim { font-family: 'DM Serif Display', serif; color: var(--accent); opacity: 0.7; line-height: 1.35; font-style: italic; transition: all 0.15s; }
  @keyframes ls-fadein { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }

  .ls-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1; gap: 12px; opacity: 0.35; user-select: none; padding-bottom: 60px; }
  .ls-empty-icon { font-size: 48px; }
  .ls-empty-title { font-family: 'DM Serif Display', serif; font-size: 22px; color: var(--muted); }
  .ls-empty-sub { font-size: 13px; color: var(--muted); text-align: center; max-width: 300px; line-height: 1.6; }

  .ls-waveform { display: flex; align-items: center; justify-content: center; gap: 4px; height: 50px; opacity: 0; transition: opacity 0.3s; flex-shrink: 0; }
  .ls-waveform.active { opacity: 1; }
  .ls-wave-bar { width: 4px; background: var(--accent); border-radius: 2px; height: 8px; animation: ls-wave 0.8s ease-in-out infinite; opacity: 0.6; }
  @keyframes ls-wave { 0%,100%{height:6px} 50%{height:28px} }
  .ls-wave-bar:nth-child(1){animation-delay:0.00s} .ls-wave-bar:nth-child(2){animation-delay:0.10s}
  .ls-wave-bar:nth-child(3){animation-delay:0.20s} .ls-wave-bar:nth-child(4){animation-delay:0.30s}
  .ls-wave-bar:nth-child(5){animation-delay:0.40s} .ls-wave-bar:nth-child(6){animation-delay:0.30s}
  .ls-wave-bar:nth-child(7){animation-delay:0.20s} .ls-wave-bar:nth-child(8){animation-delay:0.10s}
  .ls-wave-bar:nth-child(9){animation-delay:0.00s}

  /* POST-SESSION BAR */
  .ls-post-bar {
    position: relative; z-index: 10;
    display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
    padding: 12px 24px;
    border-top: 1px solid var(--border);
    background: rgba(18,21,28,0.95);
    backdrop-filter: blur(10px);
    animation: ls-fadein 0.3s ease;
  }
  .ls-post-label { font-size: 11px; color: var(--muted); text-transform: uppercase; letter-spacing: 0.8px; white-space: nowrap; }
  .ls-divider { width: 1px; height: 20px; background: var(--border); flex-shrink: 0; }

  .ls-lang-row { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  .ls-lang-tag {
    display: flex; align-items: center; gap: 4px;
    padding: 4px 10px; border-radius: 6px; font-size: 12px;
    font-family: 'DM Mono', monospace;
    border: 1px solid var(--border); background: var(--surface); color: var(--text);
    cursor: pointer; transition: all 0.15s; user-select: none;
  }
  .ls-lang-tag.selected { border-color: var(--gold); color: var(--gold); background: rgba(200,151,58,0.1); }
  .ls-lang-tag:hover { border-color: var(--muted); }

  .ls-rec-badge { display: flex; align-items: center; gap: 5px; font-size: 11px; font-family: 'DM Mono', monospace; color: #ef5350; letter-spacing: 0.08em; }
  .ls-rec-dot { width: 7px; height: 7px; border-radius: 50%; background: #ef5350; animation: ls-pulse 1.2s ease-in-out infinite; }

  .ls-toast {
    position: fixed; bottom: 20px; left: 50%;
    transform: translateX(-50%) translateY(80px);
    background: var(--surface); border: 1px solid var(--border);
    border-radius: 10px; padding: 10px 20px; font-size: 13px; color: var(--text);
    z-index: 9000; transition: transform 0.3s ease;
    box-shadow: 0 8px 32px rgba(0,0,0,0.4); pointer-events: none;
  }
  .ls-toast.show  { transform: translateX(-50%) translateY(0); }
  .ls-toast.error { border-color: var(--error); color: var(--error); }
`;

// ─── Constants ────────────────────────────────────────────────────────────────
const LANG_OPTIONS = [
  { code: "en", label: "EN" },
  { code: "ar", label: "AR" },
  { code: "fr", label: "FR" },
  { code: "es", label: "ES" },
  { code: "de", label: "DE" },
];

const STATUS = {
  READY:      { label: "Ready",       cls: "" },
  CONNECTING: { label: "Connecting…", cls: "" },
  LIVE:       { label: "LIVE",        cls: "live" },
  STOPPED:    { label: "Stopped",     cls: "" },
  ERROR:      { label: "Error",       cls: "error" },
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function LiveScript() {
  const [apiKey, setApiKey]             = useState("ASSEMBLYAI_KEY");
  const [fontSize, setFontSize]         = useState(52);
  const [status, setStatus]             = useState(STATUS.READY);
  const [isLive, setIsLive]             = useState(false);
  const [lines, setLines]               = useState([]);
  const [toast, setToast]               = useState({ msg: "", show: false, error: false });
  const [sessionEnded, setSessionEnded] = useState(false);
  const [audioBlob, setAudioBlob]       = useState(null);
  const [selectedLangs, setSelectedLangs] = useState(["en"]);
  const [showSummary, setShowSummary]   = useState(false);

  // Audio pipeline refs
  const socketRef      = useRef(null);
  const streamRef      = useRef(null);
  const audioCtxRef    = useRef(null);
  const processorRef   = useRef(null);
  const sourceRef      = useRef(null);
  // Recording refs
  const mediaRecRef    = useRef(null);
  const audioChunksRef = useRef([]);
  // Transcript accumulator (plain text for summary)
  const transcriptRef  = useRef("");
  // Line management refs
  const interimIdRef   = useRef(null);
  const lineIdRef      = useRef(0);
  const scrollRef      = useRef(null);

  // Auto-scroll on new lines
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [lines]);

  const showToast = useCallback((msg, isError = false) => {
    setToast({ msg, show: true, error: isError });
    setTimeout(() => setToast(t => ({ ...t, show: false })), 3000);
  }, []);

  const nextId = () => ++lineIdRef.current;

  // Commit a final transcript line
  const addFinal = useCallback((text) => {
    if (!text.trim()) return;
    transcriptRef.current += (transcriptRef.current ? " " : "") + text.trim();
    setLines(prev => {
      if (interimIdRef.current !== null) {
        const id = interimIdRef.current;
        interimIdRef.current = null;
        return prev.map(l => l.id === id ? { ...l, text, final: true } : l);
      }
      return [...prev, { id: nextId(), text, final: true }];
    });
  }, []);

  // Update the live interim line
  const updateInterim = useCallback((text) => {
    if (!text.trim()) return;
    setLines(prev => {
      if (interimIdRef.current !== null) {
        return prev.map(l => l.id === interimIdRef.current ? { ...l, text } : l);
      }
      const id = nextId();
      interimIdRef.current = id;
      return [...prev, { id, text, final: false }];
    });
  }, []);

  const stop = useCallback(() => {
    if (sourceRef.current)    { sourceRef.current.disconnect();    sourceRef.current = null; }
    if (processorRef.current) { processorRef.current.disconnect(); processorRef.current = null; }
    if (audioCtxRef.current)  { audioCtxRef.current.close();       audioCtxRef.current = null; }
    if (streamRef.current)    { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (socketRef.current)    { socketRef.current.close();         socketRef.current = null; }

    // Stopping MediaRecorder triggers onstop → blob assembly
    if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") {
      mediaRecRef.current.stop();
    }

    // Commit any dangling interim line as final
    if (interimIdRef.current !== null) {
      setLines(prev => prev.map(l =>
        l.id === interimIdRef.current ? { ...l, final: true } : l
      ));
      interimIdRef.current = null;
    }

    setIsLive(false);
    setStatus(STATUS.STOPPED);
    setSessionEnded(true);
  }, []);

  const start = useCallback(async () => {
    const key = apiKey.trim();
    if (!key) { showToast("Please enter your AssemblyAI API key", true); return; }

    setStatus(STATUS.CONNECTING);
    setSessionEnded(false);
    setAudioBlob(null);
    audioChunksRef.current = [];
    transcriptRef.current = "";

    try {
      // Exchange API key for short-lived WebSocket token
      const tokenRes = await fetch(
        "https://us-central1-kings-app-74847.cloudfunctions.net/getAssemblyAIToken",
        { method: "POST", headers: { "content-type": "application/json" } }
        );
      if (!tokenRes.ok) throw new Error(`Token request failed (${tokenRes.status})`);
      const { token } = await tokenRes.json();
        const ws = new WebSocket(
        `wss://api.assemblyai.com/v2/realtime/ws?sample_rate=16000&api_key=${token}`
        );  
      socketRef.current = ws;

      ws.onopen = async () => {
        setStatus(STATUS.LIVE);
        setIsLive(true);

        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;

        // ── MediaRecorder — saves full audio to blob ──────────────
        const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : "audio/webm";
        const rec = new MediaRecorder(stream, { mimeType });
        mediaRecRef.current = rec;
        rec.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
        rec.onstop = () => {
          const blob = new Blob(audioChunksRef.current, { type: mimeType });
          setAudioBlob(blob);
        };
        rec.start(1000); // chunk every second

        // ── AudioContext → 16kHz PCM → AssemblyAI WebSocket ──────
        const ctx = new AudioContext({ sampleRate: 16000 });
        audioCtxRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        sourceRef.current = source;
        const processor = ctx.createScriptProcessor(4096, 1, 1);
        processorRef.current = processor;

        processor.onaudioprocess = (e) => {
          if (socketRef.current?.readyState === WebSocket.OPEN) {
            const f32 = e.inputBuffer.getChannelData(0);
            const i16 = new Int16Array(f32.length);
            for (let i = 0; i < f32.length; i++) {
              i16[i] = Math.max(-32768, Math.min(32767, f32[i] * 32768));
            }
            socketRef.current.send(i16.buffer);
          }
        };

        source.connect(processor);
        processor.connect(ctx.destination);
      };

      ws.onmessage = (evt) => {
        const msg = JSON.parse(evt.data);
        if (!msg.text) return;
        if (msg.message_type === "PartialTranscript") updateInterim(msg.text);
        else if (msg.message_type === "FinalTranscript") addFinal(msg.text);
      };

      ws.onerror = () => {
        setStatus(STATUS.ERROR);
        showToast("WebSocket error — check your API key", true);
        stop();
      };

      ws.onclose = () => { /* handled by stop() */ };

    } catch (err) {
      setStatus(STATUS.ERROR);
      showToast(err.message, true);
      setIsLive(false);
    }
  }, [apiKey, addFinal, updateInterim, stop, showToast]);

  const clear = useCallback(() => {
    setLines([]);
    interimIdRef.current = null;
    transcriptRef.current = "";
    setSessionEnded(false);
    setAudioBlob(null);
  }, []);

  const downloadAudio = useCallback(() => {
    if (!audioBlob) return;
    const url = URL.createObjectURL(audioBlob);
    const a = document.createElement("a");
    a.href = url;
    const ts = new Date().toISOString().slice(0, 16).replace("T", "_").replace(":", "-");
    a.download = `lesson_audio_${ts}.webm`;
    a.click();
    URL.revokeObjectURL(url);
  }, [audioBlob]);

  // Toggle language selection — max 2 (bilingual support matches LessonSummaryModal)
  const toggleLang = (code) => {
    setSelectedLangs(prev => {
      if (prev.includes(code)) return prev.length > 1 ? prev.filter(l => l !== code) : prev;
      if (prev.length >= 2) return [prev[1], code]; // slide window of 2
      return [...prev, code];
    });
  };

  const hasContent    = lines.length > 0;
  const hasTranscript = transcriptRef.current.length > 0;

  return (
    <>
      <style>{STYLES}</style>
      <div className="ls-root">

        {/* ── TOP BAR ───────────────────────────────────────────── */}
        <div className="ls-topbar">
          <div className="ls-brand">
            <div className="ls-brand-icon">🎙</div>
            <div className="ls-brand-name">Live<span>Script</span></div>
          </div>

          <div className="ls-api-row">
            <span className="ls-api-label">API Key</span>
            <input
              className="ls-api-input"
              type="password"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder="AssemblyAI key…"
            />
          </div>

          <div className="ls-font-control">
            <span className="ls-font-label">Size</span>
            <input
              className="ls-slider"
              type="range" min={24} max={96} value={fontSize}
              onChange={e => setFontSize(Number(e.target.value))}
            />
          </div>

          <div className="ls-controls">
            {isLive && (
              <div className="ls-rec-badge">
                <div className="ls-rec-dot" /> REC
              </div>
            )}
            <div className={`ls-status ${status.cls}`}>
              <div className={`ls-dot ${status.cls === "live" ? "pulse" : ""}`} />
              {status.label}
            </div>
            <button className="ls-btn ls-btn-primary" onClick={start} disabled={isLive}>
              ▶ Start
            </button>
            <button className="ls-btn ls-btn-stop" onClick={stop} disabled={!isLive}>
              ■ Stop
            </button>
            <button className="ls-btn ls-btn-clear" onClick={clear}>
              ✕ Clear
            </button>
          </div>
        </div>

        {/* ── MAIN DISPLAY ──────────────────────────────────────── */}
        <div className="ls-display">
          <div className="ls-transcript-wrapper">
            {!hasContent ? (
              <div className="ls-empty">
                <div className="ls-empty-icon">👩‍🏫</div>
                <div className="ls-empty-title">Ready to transcribe</div>
                <div className="ls-empty-sub">
                  Press Start and begin speaking. Words appear in real time and audio is recorded automatically.
                </div>
              </div>
            ) : (
              <div className="ls-scroll" ref={scrollRef}>
                <div className="ls-lines">
                  {lines.map(line => (
                    <div
                      key={line.id}
                      className={line.final ? "ls-line-final" : "ls-line-interim"}
                      style={{ fontSize }}
                    >
                      {line.text}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className={`ls-waveform ${isLive ? "active" : ""}`}>
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="ls-wave-bar" />
            ))}
          </div>
        </div>

        {/* ── POST-SESSION BAR ──────────────────────────────────── */}
        {sessionEnded && (
          <div className="ls-post-bar">
            <span className="ls-post-label">Session ended</span>
            <div className="ls-divider" />

            {/* Audio download */}
            {audioBlob && (
              <button className="ls-btn ls-btn-dl" onClick={downloadAudio}>
                🎧 Download Audio (.webm)
              </button>
            )}

            {/* Summary section — only if we have transcript text */}
            {hasTranscript && (
              <>
                <div className="ls-divider" />
                <span className="ls-post-label">Summary language:</span>
                <div className="ls-lang-row">
                  {LANG_OPTIONS.map(l => (
                    <div
                      key={l.code}
                      className={`ls-lang-tag ${selectedLangs.includes(l.code) ? "selected" : ""}`}
                      onClick={() => toggleLang(l.code)}
                    >
                      {l.label}
                      {selectedLangs.includes(l.code) && (
                        <span style={{ fontSize: 9, opacity: 0.6 }}>
                          {selectedLangs.length === 2
                            ? (selectedLangs.indexOf(l.code) === 0 ? "①" : "②")
                            : "✓"}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  className="ls-btn ls-btn-gold"
                  onClick={() => setShowSummary(true)}
                >
                  📄 Generate Summary
                </button>
              </>
            )}
          </div>
        )}

        {/* ── TOAST ─────────────────────────────────────────────── */}
        <div className={`ls-toast ${toast.show ? "show" : ""} ${toast.error ? "error" : ""}`}>
          {toast.msg}
        </div>
      </div>

      {/* ── LESSON SUMMARY MODAL ──────────────────────────────────
          Passes the full accumulated transcript as plain text.
          language prop is a string for mono, array for bilingual —
          exactly matching what LessonSummaryModal expects.
      ─────────────────────────────────────────────────────────── */}
      {showSummary && (
        <LessonSummaryModal
          transcript={transcriptRef.current}
          language={
            selectedLangs.length === 1
              ? selectedLangs[0]
              : selectedLangs
          }
          onClose={() => setShowSummary(false)}
          onSave={(saved) => {
            // Wire up to Firestore or a parent handler here if needed
            console.log("Lesson summary saved:", saved);
          }}
        />
      )}
    </>
  );
}